import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { totalCountEvent } from 'services/generalServices';
import { createTaskStage } from 'services/TaskServices';
import { queryKeys } from '../../../config/queryClient';
import { getTaskStages } from 'services/CaseServices';

const fetchTaskStages = async () => {
  const countResponse = await totalCountEvent('task-stage');
  const pageLimit = countResponse.data;
  const response = await getTaskStages(pageLimit, true);

  return response.data || [];
};

const transformTaskStagesToColumns = (taskStages) => {
  if (!taskStages || !Array.isArray(taskStages)) {
    return [];
  }

  const activeStages = taskStages.sort((a, b) => a.position - b.position);

  const doneStage = activeStages.find((stage) => stage.task_stage_name?.toLowerCase() === 'done');
  const otherStages = activeStages.filter((stage) => stage.task_stage_name?.toLowerCase() !== 'done');

  const orderedStages = doneStage ? [...otherStages, doneStage] : otherStages;

  return orderedStages.map((stage, index) => ({
    ...stage,
    isDefault: stage.is_default,
    isLocked: stage.task_stage_name?.toLowerCase() === 'done',
  }));
};

/**
 * Hook to fetch task stages with React Query
 *
 * @returns {Object} - taskStages, isLoading, error, refetch
 */
export const useTaskStagesQuery = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.taskStages.list(),
    queryFn: fetchTaskStages,
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    cacheTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  return {
    taskStages: transformTaskStagesToColumns(data) || [],
    isTaskStageLoading: isLoading,
    error,
    refetch,
  };
};

/**
 * Hook to create a new task stage with React Query mutation
 *
 * @returns {Object} - mutation object with mutate, mutateAsync, isLoading, error, etc.
 */
export const useCreateTaskStageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await createTaskStage(data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch all task stages queries after successful creation
      queryClient.invalidateQueries(queryKeys.taskStages.all);
    },
  });
};
